import { isString, upperFirst } from 'lodash/fp';
import React, { MouseEvent, forwardRef } from 'react';

import { ReactComponent as IconExternalLink } from '@skippay/core/assets/icons/External-link.svg';
import { ReactComponent as IconLoading } from '@skippay/core/assets/illustrations/loader.svg';
import { BreadcrumbCategory, captureBreadcrumb } from '@skippay/core/global/errorTracking';

import { Button as ButtonProps, ButtonRef } from './@types/button';
import { SButton, SIcon } from './index.styled';

const Button = forwardRef(
	(
		props: ButtonProps,
		ref: React.RefObject<ButtonRef<ButtonProps['component'], ButtonProps['to']>>
	) => {
		const {
			analyticsId,
			children,
			disabled,
			testId,
			variant = 'primary',
			iconRight,
			...other
		} = props;

		const handleClickWrapper = (ev?: MouseEvent<HTMLButtonElement>) => {
			captureBreadcrumb(BreadcrumbCategory.buttonClick, `Button clicked: ${testId || 'unknown'}`, {
				testId: testId || 'unknown',
				analyticsId: analyticsId || 'none',
				variant,
			});
			props.onClick?.(ev);
		};

		return (
			<SButton
				{...other}
				disabled={disabled || props.isBusy}
				variant={variant}
				data-analytics-param={analyticsId ? `btn${upperFirst(analyticsId)}` : undefined}
				data-test-id={`btn${upperFirst(testId)}`}
				ref={ref}
				onClick={handleClickWrapper}
				id={(props.id ?? props.type === 'submit') ? 'submit' : undefined}
			>
				{props.isExternal && props.to ? (
					<span className="relative z-10 !flex flex-row items-center justify-center gap-2">
						{children}
						<IconExternalLink className="h-6 w-6" />
					</span>
				) : (
					<>
						{children}
						{props.isBusy ? (
							<SIcon position="right">
								<IconLoading />
							</SIcon>
						) : (
							iconRight && (
								<SIcon position="right">
									{isString(iconRight) ? <img src={iconRight} alt="icon" /> : iconRight}
								</SIcon>
							)
						)}
					</>
				)}
			</SButton>
		);
	}
);

export default Button;
